import type { AxiosInstance } from 'axios';

export type SaasPlanCode = 'FREE' | 'GROWTH' | 'PRO' | 'ENTERPRISE';
export type SaasBillingCycle = 'MONTHLY' | 'ANNUAL';
export type VendorSubscriptionStatus = 'TRIALING' | 'ACTIVE' | 'PAST_DUE' | 'CANCELLED' | 'EXPIRED';
export type SubscriptionInvoiceStatus = 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED' | 'CANCELLED';
export type SponsoredListingStatus = 'PAUSED' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';

export interface SaasPlan {
  id: string;
  code: SaasPlanCode;
  name: string;
  monthlyPricePaise: number;
  annualPricePaise: number;
  productCap: number;
  mediaPerProductCap: number;
  sponsoredFeeBps: number;
  features: Record<string, boolean>;
  active: boolean;
  createdAt: string;
}

export interface VendorSubscription {
  id: string;
  vendorId: string;
  planId: string;
  status: VendorSubscriptionStatus;
  billingCycle: SaasBillingCycle;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt: string | null;
  autoRenew: boolean;
  providerSubscriptionId: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
}

export interface Entitlement {
  vendorId: string;
  planCode: SaasPlanCode;
  status: VendorSubscriptionStatus;
  productCap: number;
  mediaPerProductCap: number;
  sponsoredFeeBps: number;
  features: Record<string, boolean>;
  active: boolean;
  currentPeriodEnd: string;
}

export interface SubscriptionInvoice {
  id: string;
  invoiceNumber: string;
  subscriptionId: string;
  periodStart: string;
  periodEnd: string;
  amountPaise: number;
  taxPaise: number;
  totalPaise: number;
  status: SubscriptionInvoiceStatus;
  dueAt: string;
  paidAt: string | null;
  providerPaymentId: string | null;
  failureReason: string | null;
  createdAt: string;
}

export interface SponsoredListing {
  id: string;
  vendorId: string;
  productId: string;
  status: SponsoredListingStatus;
  bidPerClickPaise: number;
  totalBudgetPaise: number;
  spentPaise: number;
  impressions: number;
  clicks: number;
  startsAt: string;
  endsAt: string | null;
  pausedReason: string | null;
  createdAt: string;
}

export interface SubscribeRequest {
  vendorId: string;
  planCode: SaasPlanCode;
  billingCycle?: SaasBillingCycle;
  trialDays?: number;
  autoRenew?: boolean;
}

export function saasEndpoints(http: AxiosInstance) {
  return {
    // Plans
    listPlans: () => http.get<{ data: SaasPlan[] }>('/saas/plans').then((r) => r.data.data),
    upsertPlan: (body: {
      code: SaasPlanCode;
      name: string;
      monthlyPricePaise: number;
      annualPricePaise?: number;
      productCap: number;
      mediaPerProductCap?: number;
      sponsoredFeeBps?: number;
      features?: Record<string, boolean>;
      active?: boolean;
    }) => http.put<SaasPlan>('/admin/saas/plans', body).then((r) => r.data),

    // Vendor self
    mySubscription: () => http.get<VendorSubscription>('/saas/subscription/me').then((r) => r.data),
    myEntitlement: () => http.get<Entitlement>('/saas/entitlement/me').then((r) => r.data),
    subscribe: (body: SubscribeRequest) =>
      http.post('/saas/subscription/me', body).then((r) => r.data),
    changeMyPlan: (body: { planCode: SaasPlanCode; billingCycle?: SaasBillingCycle }) =>
      http.post('/saas/subscription/me/change-plan', body).then((r) => r.data),
    cancelMine: (body: { reason: string; immediate?: boolean }) =>
      http.post('/saas/subscription/me/cancel', body).then((r) => r.data),
    myInvoices: () =>
      http.get<{ data: SubscriptionInvoice[] }>('/saas/invoices/me').then((r) => r.data.data),

    // Admin
    adminListSubscriptions: () =>
      http
        .get<{ data: VendorSubscription[] }>('/admin/saas/subscriptions')
        .then((r) => r.data.data),
    adminSubscribe: (body: SubscribeRequest) =>
      http.post('/admin/saas/subscriptions', body).then((r) => r.data),
    adminEntitlement: (vendorId: string) =>
      http.get<Entitlement>(`/admin/saas/entitlement/${vendorId}`).then((r) => r.data),
    adminChangePlan: (
      vendorId: string,
      body: { planCode: SaasPlanCode; billingCycle?: SaasBillingCycle },
    ) => http.post(`/admin/saas/subscriptions/${vendorId}/change-plan`, body).then((r) => r.data),
    adminCancel: (vendorId: string, body: { reason: string; immediate?: boolean }) =>
      http.post(`/admin/saas/subscriptions/${vendorId}/cancel`, body).then((r) => r.data),
    adminListInvoices: () =>
      http.get<{ data: SubscriptionInvoice[] }>('/admin/saas/invoices').then((r) => r.data.data),
    payInvoice: (invoiceId: string, body: { providerPaymentId?: string }) =>
      http.post(`/admin/saas/invoices/${invoiceId}/pay`, body).then((r) => r.data),
    adminRecoverSubscription: (vendorId: string) =>
      http.post(`/admin/saas/subscriptions/${vendorId}/recover`).then((r) => r.data),
    runRenewals: () => http.post('/admin/saas/renewals/run').then((r) => r.data),

    // Sponsored listings (vendor self)
    mySponsored: () =>
      http.get<{ data: SponsoredListing[] }>('/saas/sponsored/me').then((r) => r.data.data),
    createSponsored: (body: {
      productId: string;
      bidPerClickPaise: number;
      totalBudgetPaise: number;
      startsAt?: string;
      endsAt?: string;
    }) => http.post<SponsoredListing>('/saas/sponsored', body).then((r) => r.data),
    pauseSponsored: (listingId: string, reason: string) =>
      http.post(`/saas/sponsored/${listingId}/pause`, { reason }).then((r) => r.data),
    resumeSponsored: (listingId: string) =>
      http.post(`/saas/sponsored/${listingId}/resume`).then((r) => r.data),
  };
}

export type SaasEndpoints = ReturnType<typeof saasEndpoints>;
